import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import toast from 'react-hot-toast';

const vdaImage  = '/assets/banner.png';
const logoImage = '/assets/logo.png';

const NOC_STEPS = [
  { icon: '📝', num: '01', title: 'Apply Online',   desc: 'Submit NOC application with required documents' },
  { icon: '🏢', num: '02', title: 'Dept Review',    desc: 'Related department reviews your application' },
  { icon: '✅', num: '03', title: 'Final Approval', desc: 'Final authority approves and issues NOC' },
  { icon: '⬇️', num: '04', title: 'Download NOC',  desc: 'Download your approved NOC certificate' },
];

export default function RegisterPage() {
  const [form, setForm]       = useState({ name: '', mobile: '', email: '', address: '' });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!/^[6-9]\d{9}$/.test(form.mobile)) {
      return toast.error('Enter valid 10-digit mobile number');
    }
    setLoading(true);
    try {
      await api.post('/auth/register', form);
      toast.success('Registration successful! Please login.');
      navigate('/citizen-login');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Registration failed');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex">

      {/* ── LEFT — Banner + Steps ──────────────────────────── */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-3/5 relative flex-col"
        style={{ background: 'linear-gradient(135deg, #1a365d 0%, #2b6cb0 60%, #1e40af 100%)' }}>

        <div className="absolute inset-0 opacity-10"
          style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px' }} />

        <div className="relative z-10 flex flex-col h-full p-10">

          {/* Banner Image */}
          <div className="rounded-2xl overflow-hidden shadow-2xl mb-8 flex-shrink-0" style={{ maxHeight: '220px' }}>
            <img src={vdaImage} alt="VDA" className="w-full h-full object-cover"
              onError={e => {
                e.target.style.display = 'none';
                e.target.parentNode.style.background = '#c53030';
                e.target.parentNode.innerHTML =
                  '<div style="display:flex;flex-direction:column;align-items:center;justify-content:center;height:220px;color:white">' +
                  '<span style="font-size:3rem;font-weight:900">VDA</span>' +
                  '<span style="font-size:1rem;margin-top:8px;font-weight:600">Citizen NOC Portal</span>' +
                  '<span style="font-size:0.85rem;margin-top:4px;opacity:0.8">Varanasi Development Authority</span>' +
                  '</div>';
              }} />
          </div>

          {/* How NOC Works */}
          <div className="mb-6">
            <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-4 opacity-80">
              How NOC Application Works
            </h3>
            <div className="space-y-3">
              {NOC_STEPS.map((step, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center text-lg flex-shrink-0">
                    {step.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium text-sm">{step.title}</p>
                    <p className="text-blue-200 text-xs">{step.desc}</p>
                  </div>
                  <div className="w-7 h-7 bg-white/10 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                    {step.num}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Application Process */}
          <div className="mt-auto">
            <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-4 opacity-80">
              Application Process
            </h3>
            <div className="flex gap-3">
              {NOC_STEPS.map((step, i) => (
                <div key={i} className="flex-1 bg-white/10 rounded-xl p-3 text-center">
                  <div className="text-2xl mb-1">{step.icon}</div>
                  <p className="text-white font-bold text-xs">{step.num}</p>
                  <p className="text-white font-semibold text-xs mt-0.5">{step.title}</p>
                </div>
              ))}
            </div>
            <div className="flex gap-3 mt-2">
              {NOC_STEPS.map((step, i) => (
                <div key={i} className="flex-1 text-center">
                  <p className="text-blue-200 text-xs leading-tight">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>

      {/* ── RIGHT — Register Form ─────────────────────────── */}
      <div className="w-full lg:w-1/2 xl:w-2/5 flex items-center justify-center p-6 bg-gray-50">
        <div className="w-full max-w-sm">

          {/* Mobile logo */}
          <div className="lg:hidden text-center mb-8">
            <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-lg mx-auto mb-3 overflow-hidden">
              <img src={logoImage} alt="VDA" className="w-12 h-12 object-contain"
                onError={e => {
                  e.target.style.display = 'none';
                  e.target.parentNode.style.background = '#c53030';
                  e.target.parentNode.innerHTML = '<span style="color:white;font-weight:900;font-size:1.2rem">VDA</span>';
                }} />
            </div>
            <h1 className="text-xl font-bold text-gray-800">Citizen Portal</h1>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-800">Create Account</h2>
              <p className="text-gray-500 text-sm mt-1">Register to apply for NOC</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="form-label">Full Name <span className="text-red-500">*</span></label>
                <input type="text" className="form-input"
                  placeholder="Enter your full name"
                  value={form.name}
                  onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                  required autoFocus />
              </div>

              <div>
                <label className="form-label">Mobile Number <span className="text-red-500">*</span></label>
                <div className="flex">
                  <span className="inline-flex items-center px-3 border border-r-0 border-gray-300 rounded-l-lg bg-gray-50 text-gray-500 text-sm font-medium">
                    +91
                  </span>
                  <input type="tel" className="form-input rounded-l-none flex-1"
                    placeholder="10-digit mobile number"
                    value={form.mobile}
                    onChange={e => setForm(p => ({ ...p, mobile: e.target.value.replace(/\D/,'').slice(0,10) }))}
                    maxLength={10} required />
                </div>
                {form.mobile.length > 0 && form.mobile.length < 10 && (
                  <p className="text-xs text-orange-500 mt-1">{form.mobile.length}/10 digits</p>
                )}
              </div>

              <div>
                <label className="form-label">Email Address</label>
                <input type="email" className="form-input"
                  placeholder="email@example.com"
                  value={form.email}
                  onChange={e => setForm(p => ({ ...p, email: e.target.value }))} />
              </div>

              <div>
                <label className="form-label">Address</label>
                <textarea className="form-input" rows={2}
                  placeholder="House no., Street, Area"
                  value={form.address}
                  onChange={e => setForm(p => ({ ...p, address: e.target.value }))} />
              </div>

              <button type="submit" disabled={loading}
                className="w-full py-3 rounded-xl text-white font-semibold text-sm disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg, #1a365d, #2b6cb0)' }}>
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Registering...
                  </span>
                ) : '✓ Register'}
              </button>
            </form>

            <div className="mt-5 pt-4 border-t border-gray-100 text-center">
              <Link to="/citizen-login"
                className="text-sm text-blue-600 hover:text-blue-800 hover:underline font-medium">
                Already registered? Login →
              </Link>
            </div>
          </div>

          <p className="text-center text-gray-400 text-xs mt-5">
            © {new Date().getFullYear()} Varanasi Development Authority
          </p>
        </div>
      </div>
    </div>
  );
}